/**
 * Ryan O'Dowd
 * 2022-04-07
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Image,
  ScrollView,
  View,
} from 'react-native';
import {
  PRACTICE_ROUTE,
  addStormLesson,
  // @TODO: checkForNewBadges,
  // @TODO: clearAllNewBadges,
  setTimestampLastAskedForReview,
} from '@utils/store/actions';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '@components/common/Button';
import DailyRings from '@components/DailyRings';
import Icon from '@react-native-vector-icons/material-design-icons';
import InAppReview from './InAppReview';
import {
  ProgressBar,
} from 'react-native-paper';
import StreakRepairDisplay from '@components/StreakRepairDisplay';
import Text from '@components/common/Text';
import fb from '@utils/firebase';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const BRAIN_STANDARD = require('@assets/img/characters/brain-standard-shadow.png');
const BRAIN_SUCCESS = require('@assets/img/characters/brain-success-shadow.png');
const ELEPHANT_STANDARD = require('@assets/img/characters/elephant-standard-shadow.png');
const ELEPHANT_SUCCESS = require('@assets/img/characters/elephant-success-shadow.png');

const SummaryScreen = (props: SummaryScreenProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  // @TODO: const newBadges = hooks.useTypedSelector((state) => state.newBadges);

  // @TODO: useEffect(() => {
  // @TODO:   dispatch(checkForNewBadges()); // @TODO: technically, this doesn't include peanuts/etc. earned this lesson...won't get the badge until the next lesson

  // @TODO:   return () => dispatch(clearAllNewBadges());
  // @TODO: }, [dispatch]);

  const haveSavedLesson = useRef(false);
  const alreadyAskedForReview = useRef(false);

  const timestampLastAskedForReview = hooks.useTypedSelector((state) => state.timestampLastAskedForReview);

  const [_successDelayMs, setSuccessDelayMs] = useState(500);

  useEffect(() => {
    const twoMonthsMs = 2 * 30 * 24 * 60 * 60 * 1000;
    if (!alreadyAskedForReview.current && props.isTest && props.passedTestVerseNums.length && ((Date.now() - timestampLastAskedForReview) > twoMonthsMs)) {
      alreadyAskedForReview.current = true;
      InAppReview.RequestInAppReview().then((hasFlowFinishedSuccessfully) => {
        if (hasFlowFinishedSuccessfully) {
          dispatch(setTimestampLastAskedForReview(Date.now()));
        }
      }).catch((error) => {
        fb.recordError(fb.crashlytics, error);
      });
    }
  }, [props.isTest, props.passedTestVerseNums.length, timestampLastAskedForReview, dispatch]);

  useEffect(() => {
    if (_successDelayMs <= 0) {
      return;
    }

    const refreshMs = 100;
    const intervalId = setInterval(() => {
      setSuccessDelayMs((curr) => curr - refreshMs);
    }, refreshMs);

    return () => clearInterval(intervalId);
  }, [_successDelayMs]);

  const getGraphic = () => {
    let otherStyles = styles.elephantGraphic;
    let imgSrc = ELEPHANT_STANDARD;

    if (props.isTest) {
      otherStyles = styles.brainGraphic;
      imgSrc = BRAIN_STANDARD;
      if (props.passedTestVerseNums.length && _successDelayMs <= 0) {
        imgSrc = BRAIN_SUCCESS;
      }
    } else if (props.numCorrectAnswers >= Math.ceil(props.numTotalAnswers / 2) && _successDelayMs <= 0) {
      imgSrc = ELEPHANT_SUCCESS;
    }

    return (
      <Image
        style={[styles.graphic, otherStyles]}
        source={imgSrc}
      />
    );
  };

  useEffect(() => {
    if (!haveSavedLesson.current) {
      props.onComplete();
      haveSavedLesson.current = true;
    }
  }, [props, dispatch]);

  const _renderTextRow = (leftText, rightText, extraTextStyles = undefined) => {
    return (
      <View style={styles.infoRow}>
        <Text style={[styles.text, extraTextStyles]}>{leftText}</Text>
        <Text style={[styles.text, extraTextStyles]}>{rightText}</Text>
      </View>
    );
  };

  useEffect(() => {
    if ((props.numTotalAnswers === 10 && props.numCorrectAnswers >= 8) || props.passedTestVerseNums.length >= 1) { // @TODO: don't hardcode 10 (but need to make sure this is a full lesson)
      dispatch(addStormLesson());
    }
  }, [props.passedTestVerseNums, props.numTotalAnswers, props.numCorrectAnswers, dispatch]);

  const encouragingText = useMemo(() => {
    const percentCorrect = props.numCorrectAnswers / props.numTotalAnswers * 100;
    // @TODO: these could be improved
    let encouragingPhrases = [];
    if (percentCorrect === 100) {
      encouragingPhrases = [i18n('encouragement100_1'), i18n('encouragement100_2')];
    } else if (percentCorrect >= 75) {
      encouragingPhrases = [i18n('encouragement75_1'), i18n('encouragement75_2'), i18n('encouragement75_3')];
    } else if (percentCorrect >= 50) {
      encouragingPhrases = [i18n('encouragement50_1'), i18n('encouragement50_2'), i18n('encouragement50_3')];
    } else {
      encouragingPhrases = [i18n('encouragement0_1'), i18n('encouragement0_2'), i18n('encouragement0_3')];
    }

    return encouragingPhrases[Math.floor(Math.random() * encouragingPhrases.length)];
  }, [props.numCorrectAnswers, props.numTotalAnswers, i18n]);

  const streaks = hooks.useTypedSelector((state) => state.streaks);
  const showStreakRepair = !props.isTest && streaks.data.some((streak) => streak.repairable);

  // @TODO: there's too much text in this sumary...replace text with graphics and/or consolidate rows of information
  return (
    <View style={styles.container}>
      <View style={styles.innerContainer}>
        <ScrollView
          contentContainerStyle={styles.innerContainerScroll}
          showsVerticalScrollIndicator={false}
        >
          {getGraphic()}
          <View style={styles.summaryTextWrapper}>
            {!props.isTest &&
              <>
                <Text style={styles.encouragingText}>{encouragingText}</Text>
                <View style={styles.progressBarWrapper}>
                  <ProgressBar style={styles.progressBar} progress={props.numCorrectAnswers / props.numTotalAnswers} color={styles.colors.accent} />
                  <Text style={styles.progressText}>{`${Math.round(props.numCorrectAnswers / props.numTotalAnswers * 100)}%`}</Text>
                </View>
              </>
            }
            {props.isTest && (props.passedTestVerseNums.length
              ? <Text style={styles.encouragingText}>{i18n('successCount', props.passedTestVerseNums.length, props.passedTestVerseNums.length === 1 ? i18n('verseLowercase') : i18n('versesLowercase'))}</Text>
              : <Text style={styles.encouragingText}>{i18n('perfectScoreRequired')}</Text>
            )}
            {_renderTextRow(`${i18n('peanutsEarned')}:`, props.numCorrectAnswers)}{/* @TODO: peanuts could be graphic instead of text */}
            {!!props.perfectBonus && _renderTextRow(`${i18n('bonus')}:`, props.perfectBonus, styles.bonusRow)}
            {!!props.multiVerseBonus && _renderTextRow(`${i18n('multiVerseBonus')}:`, props.multiVerseBonus, styles.bonusRow)}
            {!props.isTest && _renderTextRow(`${i18n('difficultyMultiplier')} (${i18n(`difficulty${utilities.capitalizeFirstLetter(props.difficulty)}`)}):`, `${props.difficultyBonus}x`)}{/* @TODO: make sure this is displayed correctly */}
            <View style={styles.totalSeparator} />
            {!!props.passageOfTheDayBonus && _renderTextRow(`${i18n('passageOfTheDayBonus')}:`, props.passageOfTheDayBonus, styles.bonusRow)}
            {(props.totalPeanutsEarned > 0 && props.stormMultiplier !== 1) && _renderTextRow(`Storm multiplier:`, `${props.stormMultiplier}x`)}
            {!!props.levelUpBonus && _renderTextRow(`${i18n('levelUpBonus')}:`, props.levelUpBonus, styles.bonusRow)}
            {_renderTextRow(`${i18n('grandTotal')}:`, props.totalPeanutsEarned, styles.finalRow)}{/* @TODO: animate all of these, but especially this one */}
            {/* @TODO:
            {!props.isTest && _renderTextRow(`${i18n('lifetimePeanutsEarned')}:`, user.lifetime_peanuts, styles.lifetimeRow)}{/* @TODO: animate all of these, but especially this one /}
            */}
            <View style={styles.belowWrapper}>
              <View style={styles.dailyRingsWrapper}>
                <Text style={[styles.streakGraphTitle, styles.dailyRingsTitle]}>{i18n('dailyTargets')}</Text>
                <DailyRings />
              </View>
              {showStreakRepair && <StreakRepairDisplay repairStreakDate={props.repairStreakDate} practiceIsMounted={true} />}
            </View>
          </View>
          {/* @TODO:
          {newBadges.map((newBadge) => {
            return (
              <Badge
                key={`${newBadge.category}${newBadge.threshold}`}
                category={newBadge.category}
                dateEarned={newBadge.date_earned}
                description={newBadge.description}
                threshold={newBadge.threshold}
              />
            );
          })}
          */}
        </ScrollView>
      </View>
      <View style={styles.buttonsWrapper}>
        <View style={styles.buttonWrapper}>
          <Button
            mode='contained'
            isLarge={true}
            theme={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primaryDark}
            onPress={() => navigation.goBack()} // @TODO: there is a bug here on first login...if user doesn't get a peanut, this goes to /app/learn and errors out instead of going to /app/goal
          >
            {i18n('finishLesson')}
          </Button>
        </View>
        <View style={styles.buttonWrapper}>
          <Button
            mode='contained'
            isLarge={true}
            theme={styles.colors.accent}
            onPress={() => {
              // @TODO: there is a race condition here where i think it's the network call to save the lesson has to finish before the again button is pressed, otherwise the Practice component will re-render (and new games will be selected) resulting in lessons flashing/cycling through quickly
              // @TODO: maybe this button should occasionally incentivize double peanuts
              navigation.replace(PRACTICE_ROUTE, {
                passages: props.passages,
                isTest: props.isTest,
                repairStreakDate: props.repairStreakDate,
              });
            }}
          >
            <Icon style={styles.playAgainIcon} name='refresh' /> {props.isTest ? i18n('again') : i18n('newGame')}{/* @TODO: icon prop exists, but the size is off...wonder if this can be set in the theme config? */}
          </Button>
        </View>
        {/* @TODO: add button here for increasing difficulty to medium or hard if this isn't already hard difficulty and it was a perfect lesson (or maybe two in a row) */}
        {/* @TODO: also if hard and perfect (maybe a couple of times), suggest them to take a test...have button here that takes them to it */}
      </View>
    </View>
  );
};

class SummaryScreenProps {
  difficulty: 'easy' | 'medium' | 'hard'; // @TODO: or test?
  difficultyBonus: number;
  numCorrectAnswers: number;
  numTotalAnswers: number;
  onComplete: () => void;
  perfectBonus: number; // @TODO: this should be calculated here instead of being passed in
  passageOfTheDayBonus: number; // @TODO: this should be calculated here instead of being passed in
  multiVerseBonus: number; // @TODO: this should be calculated here instead of being passed in
  levelUpBonus: number; // @TODO: this should be calculated here instead of being passed in
  totalPeanutsEarned: number; // @TODO: this should be calculated here instead of being passed in
  isTest: boolean;
  stormMultiplier: number;
  numVersesLeveledUp: number;

  passages?: any[];
  passedTestVerseNums?: number[];
  repairStreakDate: string; // @TODO: date object?
};

export default SummaryScreen;
